import { useEffect, useState, type ComponentProps, type ReactNode } from "react"

import { cn } from "@coval/ui/cn"
import {
  Activity,
  AlertCircle,
  BarChart3,
  BookOpen,
  Brain,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  MessageSquare,
  Search,
  Sparkles,
  Table2,
  Type,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react"
import { Streamdown } from "streamdown"

import type { ActionExecutionResult } from "../../hooks/useActionExecutor"
import type {
  CoviActionItemPatch,
  SofiaProposedAction,
} from "../../model/types"
import { parseChartSpec } from "../../utils/chartSpec"
import type { NotebookCell } from "../../utils/notebookCells"
import { toolLabel, toolStatusLabel } from "../../utils/toolSteps"
import { ConfirmActionCard } from "../ConfirmActionCard"
import { CoviVizCard } from "../CoviVizCard"
import {
  SOFIA_MARKDOWN_COMPONENTS,
  SofiaMarkdownLink,
} from "../SofiaMarkdownLink"
import { NotebookTable } from "./NotebookTable"

interface NotebookCellViewProps {
  cell: NotebookCell
  executeProposal: (
    proposal: SofiaProposedAction,
    confirmationText?: string,
  ) => Promise<ActionExecutionResult>
  updateActionItem: (id: string, patch: CoviActionItemPatch) => void
  remarkPlugins: ComponentProps<typeof Streamdown>["remarkPlugins"]
}

const CHART_MIN_HEIGHT = 300
const CHART_ROW_HEIGHT = 36
/** Rows a table shows before it asks to be expanded. */
const TABLE_PEEK_ROWS = 3

/** Notebook cells are wide, so give a horizontal bar chart room for every category. */
function chartHeightFor(spec: unknown): number {
  const parsed = parseChartSpec(spec)
  if (!parsed) {
    return CHART_MIN_HEIGHT
  }
  const horizontal = parsed.chart === "bar" && parsed.opts?.horizontal === true
  return horizontal
    ? Math.max(CHART_MIN_HEIGHT, parsed.data.length * CHART_ROW_HEIGHT + 96)
    : CHART_MIN_HEIGHT
}

const CELL_META: Record<
  NotebookCell["kind"],
  { label: string; icon: LucideIcon }
> = {
  prompt: { label: "Prompt", icon: Sparkles },
  research: { label: "Research", icon: Search },
  table: { label: "Table", icon: Table2 },
  visualization: { label: "Chart", icon: BarChart3 },
  narrative: { label: "Text", icon: Type },
  action: { label: "Action", icon: Zap },
  "saved-action": { label: "Action", icon: Zap },
  error: { label: "Error", icon: AlertCircle },
}

const MARKDOWN_CLASSES =
  "text-sm leading-relaxed text-coval-text [&_p]:mb-2 [&_p:last-child]:mb-0 " +
  "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 " +
  "[&_li]:mb-1 [&_a]:text-coval-info-text [&_a]:underline [&_strong]:font-semibold " +
  "[&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] " +
  "[&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:font-semibold"

/**
 * One notebook cell: a quiet card with a small type header, the way Hex draws
 * its cells. The header toggles the body; `meta` reads on the right.
 */
function Cell({
  kind,
  meta,
  tone = "default",
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  children,
}: {
  kind: NotebookCell["kind"]
  meta?: ReactNode
  tone?: "default" | "error"
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}) {
  const { label, icon: Icon } = CELL_META[kind]
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = onOpenChange ?? setUncontrolledOpen
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <section
      className={cn(
        "rounded-md border bg-surface-default transition-colors",
        tone === "error" ? "border-coval-danger/40" : "border-coval-border",
      )}
      data-notebook-cell={kind}
    >
      <header className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-coval-text-muted">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex min-w-0 items-center gap-2 rounded hover:text-coval-text"
        >
          <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
          <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="font-medium uppercase tracking-wide">{label}</span>
        </button>
        {meta ? (
          <span className="ml-auto flex min-w-0 items-center gap-2 truncate text-coval-text-muted/80">
            {meta}
          </span>
        ) : null}
      </header>
      {open ? (
        <div className="border-t border-coval-border/60 px-3 py-2.5">
          {children}
        </div>
      ) : null}
    </section>
  )
}

/** Icon for a research step, by what the tool touches. */
function toolIcon(name: string): LucideIcon {
  const lower = name.toLowerCase()
  if (lower.includes("report")) {
    return FileText
  }
  if (lower.includes("run") || lower.includes("simulation")) {
    return Activity
  }
  if (lower.includes("conversation") || lower.includes("transcript")) {
    return MessageSquare
  }
  if (
    lower.includes("playbook") ||
    lower.includes("knowledge") ||
    lower.includes("doc") ||
    lower.includes("skill") ||
    lower.includes("failure")
  ) {
    return BookOpen
  }
  if (lower.includes("memory") || lower.includes("recall")) {
    return Brain
  }
  if (
    lower.includes("resource") ||
    lower.includes("metric") ||
    lower.includes("agent") ||
    lower.includes("persona") ||
    lower.includes("test")
  ) {
    return Database
  }
  return Wrench
}

type ResearchStep = Extract<NotebookCell, { kind: "research" }>["items"][number]

/** Consecutive identical steps fold into one row with a count. */
function groupSteps(
  items: ResearchStep[],
): Array<{ step: ResearchStep; count: number }> {
  const grouped: Array<{ step: ResearchStep; count: number }> = []
  for (const step of items) {
    const last = grouped[grouped.length - 1]
    if (
      last &&
      last.step.name === step.name &&
      last.step.status === step.status &&
      last.step.running === step.running
    ) {
      last.count += 1
    } else {
      grouped.push({ step, count: 1 })
    }
  }
  return grouped
}

function ResearchCell({
  cell,
}: {
  cell: Extract<NotebookCell, { kind: "research" }>
}) {
  const running = cell.items.some((item) => item.running)
  const failed = cell.items.some((item) => item.isError)
  // Open while Sofia is still working; folds up once the steps have finished.
  const [open, setOpen] = useState(running || failed)
  useEffect(() => {
    if (!running && !failed) {
      setOpen(false)
    }
  }, [failed, running])
  const count = cell.items.length
  const summary = running
    ? `Researching ${count} ${count === 1 ? "source" : "sources"}…`
    : `Researched ${count} ${count === 1 ? "source" : "sources"}`
  return (
    <Cell kind="research" meta={summary} open={open} onOpenChange={setOpen}>
      <ul className="space-y-1">
        {groupSteps(cell.items).map(({ step, count: repeats }) => {
          const Icon = toolIcon(step.name)
          const status = toolStatusLabel(step.status)
          return (
            <li
              key={step.id}
              className={cn(
                "flex items-center gap-2 text-sm",
                step.isError ? "text-coval-danger" : "text-coval-text-muted",
              )}
            >
              <Icon
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  step.running && "animate-pulse",
                )}
                aria-hidden="true"
              />
              <span className="truncate">
                {toolLabel(step.name)}
                {repeats > 1 ? ` ×${repeats}` : ""}
              </span>
              {status ? (
                <span className="shrink-0 text-xs text-coval-text-muted/70">
                  {status}
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </Cell>
  )
}

function TableCell({
  cell,
  remarkPlugins,
}: {
  cell: Extract<NotebookCell, { kind: "table" }>
  remarkPlugins: NotebookCellViewProps["remarkPlugins"]
}) {
  const [showAll, setShowAll] = useState(cell.rows.length <= TABLE_PEEK_ROWS)
  const rows = showAll ? cell.rows : cell.rows.slice(0, TABLE_PEEK_ROWS)
  const hidden = cell.rows.length - rows.length
  return (
    <Cell
      kind="table"
      meta={[
        cell.title,
        `${cell.rows.length} ${cell.rows.length === 1 ? "row" : "rows"}`,
      ]
        .filter(Boolean)
        .join(" · ")}
    >
      <div className="relative">
        <NotebookTable
          columns={cell.columns}
          rows={rows}
          remarkPlugins={remarkPlugins}
        />
        {hidden > 0 ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-surface-default to-transparent" />
        ) : null}
      </div>
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1 text-xs text-coval-info-text hover:underline"
        >
          Show all {cell.rows.length} rows
        </button>
      ) : cell.rows.length > TABLE_PEEK_ROWS ? (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-1 text-xs text-coval-text-muted hover:underline"
        >
          Show fewer
        </button>
      ) : null}
    </Cell>
  )
}

export function NotebookCellView({
  cell,
  executeProposal,
  updateActionItem,
  remarkPlugins,
}: NotebookCellViewProps) {
  switch (cell.kind) {
    case "prompt":
      return (
        <Cell kind="prompt">
          <p className="whitespace-pre-wrap text-sm font-medium text-coval-text">
            {cell.text}
          </p>
        </Cell>
      )
    case "research":
      return <ResearchCell cell={cell} />
    case "table":
      return <TableCell cell={cell} remarkPlugins={remarkPlugins} />
    case "visualization":
      return (
        <Cell kind="visualization">
          <CoviVizCard
            item={cell.item}
            execute={executeProposal}
            update={(patch) => updateActionItem(cell.item.id, patch)}
            height={chartHeightFor(cell.item.proposal.args.spec)}
            variant="notebook"
          />
        </Cell>
      )
    case "narrative":
      return (
        <Cell kind="narrative">
          <div className={MARKDOWN_CLASSES}>
            <Streamdown
              components={SOFIA_MARKDOWN_COMPONENTS}
              remarkPlugins={remarkPlugins}
            >
              {cell.item.text}
            </Streamdown>
          </div>
        </Cell>
      )
    case "action":
      return (
        <Cell kind="action" meta="Runs after you confirm">
          <ConfirmActionCard
            item={cell.item}
            execute={executeProposal}
            update={(patch) => updateActionItem(cell.item.id, patch)}
          />
        </Cell>
      )
    case "saved-action":
      return (
        <Cell kind="saved-action" meta={cell.status}>
          <p className="text-sm text-coval-text">
            {cell.actionKind.replaceAll("_", " ")}
            {cell.detail ? (
              <span className="text-coval-text-muted"> · {cell.detail}</span>
            ) : null}
            {cell.href ? (
              <>
                {" "}
                <SofiaMarkdownLink
                  href={cell.href}
                  className="text-coval-info-text underline"
                >
                  Open
                </SofiaMarkdownLink>
              </>
            ) : null}
          </p>
        </Cell>
      )
    case "error":
      return (
        <Cell kind="error" tone="error">
          <p className="text-sm text-coval-danger">{cell.text}</p>
        </Cell>
      )
    default:
      return null
  }
}
